/**
 * InstancedEquipment.tsx
 *
 * High-performance GPU Instanced rendering for all Fab Equipment.
 * Replaces low-detail shell boxes with the REAL, HIGH-QUALITY 3D equipment models:
 * - CleanroomWorkcell (Cleanroom semiconductor workcell equipment)
 * - Awp300 (Advanced wafer processing module)
 * - IndustrialVerticalLifter3D (Vertical transfer lifter & infeed conveyor)
 *
 * Each asset is decomposed into its constituent sub-mesh parts (body, frames,
 * glass, panels, screens, beacons, chrome accents) and instanced across the Fab
 * in exactly 1 draw call per sub-mesh part.
 */

import React, { useMemo } from 'react';
import * as THREE from 'three';
import { useAppSelector } from '../../hooks/redux';
import type { EquipmentRenderDescriptor } from '../../components/canvas/equipmentSilhouettePorts';
import { resolveKitBox } from '../../components/canvas/equipmentSilhouettePorts';
import { getEquipmentKitWorldTransform } from '../../components/canvas/fabKitWorldTransform';
import { resolveEquipmentKitKind } from '../../components/canvas/equipmentKitKind';
import type { LayoutElement } from '../../types';
import {
  instancedAssetManager,
  InstancedMultiMeshGroup,
  type InstanceTransform,
  type SubMeshPart,
} from './InstancedAssetManager';
import {
  getSharedStandardMaterial,
  getIndustrialDarkChassisMaterial,
  getIndustrialWhiteMaterial,
  getBrushedAluminumMaterial,
  getPolishedChromeMaterial,
  getCleanroomGlassMaterial,
  getStatusLedMaterial,
} from '../../utils/threeResourcePool';
import { AMHS_KIT_TEMPLATE_DIMS } from './amhsKitTemplateDims';
import { getAmhsKitTemplateParts } from './amhsKitTemplateStore';
import { useAmhsKitTemplateRevision } from './useAmhsKitTemplateRevision';

// ============================================================================
// TEMPLATE BUILDERS — fallback until live kit capture registers
// ============================================================================

const WORKCELL_TEMPLATE_DIMS = AMHS_KIT_TEMPLATE_DIMS.CleanroomWorkcell;
const AWP300_TEMPLATE_DIMS = AMHS_KIT_TEMPLATE_DIMS.Awp300;
const LIFTER_TEMPLATE_DIMS = AMHS_KIT_TEMPLATE_DIMS.IndustrialVerticalLifter3D;

let cachedHmiTexture: THREE.CanvasTexture | null = null;

function getEquipmentHmiTexture(): THREE.CanvasTexture | null {
  if (cachedHmiTexture) return cachedHmiTexture;
  if (typeof document === 'undefined') return null;

  try {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 384;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Dark Navy Blue UI Background
    ctx.fillStyle = '#0284C7';
    ctx.fillRect(0, 0, 512, 384);

    // Screen header
    ctx.fillStyle = '#0369A1';
    ctx.fillRect(0, 0, 512, 64);

    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 28px "Segoe UI", Inter, Arial, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText('FAB TOOL INTERFACE', 24, 32);

    // System Status badge
    ctx.fillStyle = '#10B981';
    ctx.fillRect(390, 16, 100, 32);
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 18px "Segoe UI", Inter, Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('ONLINE', 440, 32);

    // Central UI Grid Panels
    ctx.strokeStyle = '#38BDF8';
    ctx.lineWidth = 3;
    ctx.strokeRect(24, 88, 220, 180);
    ctx.strokeRect(268, 88, 220, 180);

    // Chamber telemetry
    ctx.fillStyle = '#E0F2FE';
    ctx.font = '20px "Segoe UI", Inter, Arial, sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('CHAMBER: VACUUM', 40, 128);
    ctx.fillText('FLOW: 120 sccm', 40, 168);
    ctx.fillText('TEMP: 24.5 °C', 40, 208);

    ctx.fillText('FOUP: DOCKED', 284, 128);
    ctx.fillText('PORT: ACTIVE', 284, 168);
    ctx.fillText('BATCH: #4802', 284, 208);

    // Bottom progress bar
    ctx.fillStyle = '#075985';
    ctx.fillRect(24, 300, 464, 48);
    ctx.fillStyle = '#38BDF8';
    ctx.fillRect(24, 300, 340, 48);
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 22px "Segoe UI", Inter, Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('RECIPE PROGRESS: 75%', 256, 324);

    const tex = new THREE.CanvasTexture(canvas);
    tex.anisotropy = 4;
    tex.needsUpdate = true;
    cachedHmiTexture = tex;
    return tex;
  } catch {
    return null;
  }
}

let cachedToolBadgeTexture: THREE.CanvasTexture | null = null;

function getToolBadgeTexture(): THREE.CanvasTexture | null {
  if (cachedToolBadgeTexture) return cachedToolBadgeTexture;
  if (typeof document === 'undefined') return null;

  try {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 128;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Dark obsidian plate
    ctx.fillStyle = '#060C18';
    ctx.fillRect(0, 0, 512, 128);

    // Glowing Green border
    ctx.strokeStyle = '#10B981';
    ctx.lineWidth = 8;
    ctx.strokeRect(4, 4, 504, 120);

    ctx.fillStyle = '#10B981';
    ctx.font = 'bold 36px "Segoe UI", Inter, Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('● PROCESS TOOL · ETCH-300', 256, 44);

    ctx.fillStyle = '#38BDF8';
    ctx.font = 'bold 24px "Segoe UI", Inter, Arial, sans-serif';
    ctx.fillText('STATUS: RUNNING | PORTS: READY', 256, 88);

    const tex = new THREE.CanvasTexture(canvas);
    tex.anisotropy = 4;
    tex.needsUpdate = true;
    cachedToolBadgeTexture = tex;
    return tex;
  } catch {
    return null;
  }
}

/**
 * Builds the complete, high-fidelity CleanroomWorkcell / Fab Process Tool 3D asset hierarchy.
 * Faithfully matches the real semiconductor equipment from reference Image 2:
 * - Cleanroom white console body with lower service panel seams and ventilation grilles
 * - Left operator console with blue touchscreen monitor display, keyboard tray, push buttons
 * - 3-color tower status light on top of the left console (green, amber, red lamps)
 * - Recessed dark process chamber cavity with central FOUP load port docking stage
 * - TALL vertical elevator / exhaust chimney mast column extending up to overhead track level
 * - Top overhead interface buffer dock directly interfacing with the OHT rail
 * - Illuminated green/cyan process tool status identification badge
 */
function buildCleanroomWorkcellModel(): THREE.Group {
  const root = new THREE.Group();
  root.name = 'CleanroomWorkcellTemplate';

  const { w: width, h: height, d: depth } = WORKCELL_TEMPLATE_DIMS;
  const baseH = 0.8;
  const mainH = height - baseH;
  const consoleW = width * 0.38;
  const chamberW = width * 0.54;
  const consoleX = -width * 0.5 + consoleW * 0.5;
  const chamberX = width * 0.5 - chamberW * 0.5;

  // Premium Industrial PBR Shared Materials
  const darkChassis = getIndustrialDarkChassisMaterial();
  const whiteChassis = getIndustrialWhiteMaterial();
  const darkMetal = getSharedStandardMaterial({
    color: '#0F172A',
    metalness: 0.85,
    roughness: 0.22,
    envMapIntensity: 1.4,
  });
  const brushedMetal = getBrushedAluminumMaterial();
  const polishedChrome = getPolishedChromeMaterial();
  const cleanGlass = getCleanroomGlassMaterial('#BAE6FD', 0.22);
  const slateRibs = getSharedStandardMaterial({
    color: '#334155',
    metalness: 0.75,
    roughness: 0.30,
    envMapIntensity: 1.3,
  });
  const screenMat = getSharedStandardMaterial({
    color: '#0284C7',
    emissive: '#0284C7',
    emissiveIntensity: 0.95,
    metalness: 0.2,
    roughness: 0.15,
  });
  const ledGreen = getStatusLedMaterial('#10B981', 2.4);
  const ledAmber = getStatusLedMaterial('#F59E0B', 2.4);
  const ledRed = getStatusLedMaterial('#EF4444', 2.4);
  const darkPocket = getSharedStandardMaterial({
    color: '#0B0F19',
    metalness: 0.7,
    roughness: 0.35,
  });

  // 1. Lower Heavy Base Plinth & Leveling Pads
  const baseSkid = new THREE.Mesh(
    new THREE.BoxGeometry(width * 1.02, baseH, depth * 1.02),
    darkChassis
  );
  baseSkid.position.set(0, baseH * 0.5, 0);
  root.add(baseSkid);

  [-1, 1].forEach((sx) => {
    [-1, 1].forEach((sz) => {
      const foot = new THREE.Mesh(
        new THREE.CylinderGeometry(0.55, 0.65, 0.3, 16),
        brushedMetal
      );
      foot.position.set((width * 0.5 - 1.0) * sx, 0.15, (depth * 0.5 - 1.0) * sz);
      root.add(foot);
    });
  });

  // 2. Main Equipment Body (Cleanroom White Steel Cabinet)
  const mainCabinet = new THREE.Mesh(
    new THREE.BoxGeometry(width * 0.98, mainH * 0.52, depth * 0.96),
    whiteChassis
  );
  mainCabinet.position.set(0, baseH + mainH * 0.26, 0);
  root.add(mainCabinet);

  // Lower Service Access Door Seams & Chrome Latches
  [-width * 0.24, width * 0.24].forEach((lx) => {
    const seam = new THREE.Mesh(
      new THREE.BoxGeometry(width * 0.44, mainH * 0.42, 0.04),
      brushedMetal
    );
    seam.position.set(lx, baseH + mainH * 0.24, -depth * 0.48 - 0.02);
    root.add(seam);

    const latch = new THREE.Mesh(
      new THREE.BoxGeometry(0.9, 0.35, 0.12),
      polishedChrome
    );
    latch.position.set(lx, baseH + mainH * 0.36, -depth * 0.48 - 0.06);
    root.add(latch);
  });

  // Lower ventilation exhaust grilles
  const ventGrille = new THREE.Mesh(
    new THREE.BoxGeometry(width * 0.28, 1.2, 0.06),
    darkMetal
  );
  ventGrille.position.set(-width * 0.32, baseH + 0.8, -depth * 0.48 - 0.02);
  root.add(ventGrille);

  // 3. Left Operator Control Console (Image 2: Left console with blue monitor & buttons)
  const consoleH = mainH * 0.58;
  const consoleBody = new THREE.Mesh(
    new THREE.BoxGeometry(consoleW, consoleH, depth * 0.96),
    whiteChassis
  );
  consoleBody.position.set(consoleX, baseH + mainH * 0.52 + consoleH * 0.5 - mainH * 0.08, 0);
  root.add(consoleBody);

  const screenW = consoleW * 0.82;
  const screenH = consoleH * 0.42;
  const screenZ = -depth * 0.48 - 0.04;
  const screenY = baseH + mainH * 0.65;

  const hmiBezel = new THREE.Mesh(
    new THREE.BoxGeometry(screenW + 0.3, screenH + 0.3, 0.08),
    darkMetal
  );
  hmiBezel.position.set(consoleX, screenY, screenZ);
  root.add(hmiBezel);

  const hmiScreen = new THREE.Mesh(
    new THREE.BoxGeometry(screenW, screenH, 0.04),
    screenMat
  );
  hmiScreen.position.set(consoleX, screenY, screenZ - 0.03);
  root.add(hmiScreen);

  const hmiTex = getEquipmentHmiTexture();
  if (hmiTex) {
    const hmiTexMat = getSharedStandardMaterial({
      color: '#FFFFFF',
      map: hmiTex,
      emissive: '#FFFFFF',
      emissiveIntensity: 0.9,
      toneMapped: false,
    });
    const hmiPlane = new THREE.Mesh(
      new THREE.PlaneGeometry(screenW * 0.98, screenH * 0.98),
      hmiTexMat
    );
    hmiPlane.position.set(consoleX, screenY, screenZ - 0.06);
    hmiPlane.rotation.y = Math.PI;
    root.add(hmiPlane);
  }

  // Keyboard tray & status buttons
  const kbTray = new THREE.Mesh(
    new THREE.BoxGeometry(screenW, consoleH * 0.16, 0.14),
    brushedMetal
  );
  kbTray.position.set(consoleX, screenY - screenH * 0.65, screenZ + 0.02);
  root.add(kbTray);

  [-screenW * 0.3, 0, screenW * 0.3].forEach((bx, i) => {
    const btn = new THREE.Mesh(
      new THREE.CylinderGeometry(0.2, 0.2, 0.12, 14),
      i === 0 ? ledGreen : i === 1 ? ledAmber : ledRed
    );
    btn.rotation.x = Math.PI / 2;
    btn.position.set(consoleX + bx, screenY - screenH * 0.65, screenZ - 0.06);
    root.add(btn);
  });

  // 4. 3-Color Tower Status Light (Image 2: Signal beacon on top of left console)
  const beaconMastX = consoleX - consoleW * 0.22;
  const beaconMastZ = -depth * 0.35;
  const beaconBaseY = height;

  const beaconPole = new THREE.Mesh(
    new THREE.CylinderGeometry(0.12, 0.12, 1.8, 16),
    brushedMetal
  );
  beaconPole.position.set(beaconMastX, beaconBaseY + 0.9, beaconMastZ);
  root.add(beaconPole);

  const greenLamp = new THREE.Mesh(
    new THREE.CylinderGeometry(0.26, 0.26, 0.45, 18),
    ledGreen
  );
  greenLamp.position.set(beaconMastX, beaconBaseY + 1.8 + 0.25, beaconMastZ);
  root.add(greenLamp);

  const amberLamp = new THREE.Mesh(
    new THREE.CylinderGeometry(0.26, 0.26, 0.45, 18),
    ledAmber
  );
  amberLamp.position.set(beaconMastX, beaconBaseY + 1.8 + 0.75, beaconMastZ);
  root.add(amberLamp);

  const redLamp = new THREE.Mesh(
    new THREE.CylinderGeometry(0.26, 0.26, 0.45, 18),
    ledRed
  );
  redLamp.position.set(beaconMastX, beaconBaseY + 1.8 + 1.25, beaconMastZ);
  root.add(redLamp);

  const lampCap = new THREE.Mesh(
    new THREE.CylinderGeometry(0.28, 0.26, 0.18, 18),
    darkMetal
  );
  lampCap.position.set(beaconMastX, beaconBaseY + 1.8 + 1.55, beaconMastZ);
  root.add(lampCap);

  // 5. Center/Right Process Chamber & Load Port Bay (Image 2: Recessed chamber & FOUP dock stage)
  const chamberH = mainH * 0.54;
  const chamberY = baseH + mainH * 0.26 + chamberH * 0.5;
  const chamberZ = -depth * 0.16;

  const chamberPocket = new THREE.Mesh(
    new THREE.BoxGeometry(chamberW * 0.92, chamberH, depth * 0.55),
    darkPocket
  );
  chamberPocket.position.set(chamberX, chamberY, chamberZ);
  root.add(chamberPocket);

  const chamberFrame = new THREE.Mesh(
    new THREE.BoxGeometry(chamberW * 0.96, chamberH + 0.3, 0.14),
    whiteChassis
  );
  chamberFrame.position.set(chamberX, chamberY, -depth * 0.46);
  root.add(chamberFrame);

  // Front FOUP Load Port Docking Stage
  const stageW = chamberW * 0.78;
  const stageH = 0.55;
  const stageD = depth * 0.38;
  const stageY = baseH + mainH * 0.38;
  const stageZ = -depth * 0.45;

  const loadPortStage = new THREE.Mesh(
    new THREE.BoxGeometry(stageW, stageH, stageD),
    darkMetal
  );
  loadPortStage.position.set(chamberX, stageY, stageZ);
  root.add(loadPortStage);

  const stageDeck = new THREE.Mesh(
    new THREE.BoxGeometry(stageW * 0.92, 0.12, stageD * 0.90),
    brushedMetal
  );
  stageDeck.position.set(chamberX, stageY + stageH * 0.5 + 0.06, stageZ);
  root.add(stageDeck);

  [-stageW * 0.28, 0, stageW * 0.28].forEach((px) => {
    const pin = new THREE.Mesh(
      new THREE.CylinderGeometry(0.18, 0.25, 0.28, 14),
      polishedChrome
    );
    pin.position.set(chamberX + px, stageY + stageH * 0.5 + 0.2, stageZ);
    root.add(pin);
  });

  const shield = new THREE.Mesh(
    new THREE.BoxGeometry(chamberW * 0.90, chamberH * 0.85, 0.08),
    cleanGlass
  );
  shield.position.set(chamberX, chamberY, -depth * 0.40);
  root.add(shield);

  // 6. TALL Vertical Hoist / Chimney / Elevator Mast Column (Extending to Overhead Track)
  const mastH = 16.0;
  const mastW = width * 0.26;
  const mastD = depth * 0.32;
  const mastX = chamberX;
  const mastY = height + mastH * 0.5;

  const verticalMast = new THREE.Mesh(
    new THREE.BoxGeometry(mastW, mastH, mastD),
    whiteChassis
  );
  verticalMast.position.set(mastX, mastY, 0);
  root.add(verticalMast);

  const ribLadder = new THREE.Mesh(
    new THREE.BoxGeometry(mastW * 0.84, mastH * 0.98, 0.08),
    slateRibs
  );
  ribLadder.position.set(mastX, mastY, -mastD * 0.5 - 0.04);
  root.add(ribLadder);

  [-mastW * 0.45, mastW * 0.45].forEach((rx) => {
    const rail = new THREE.Mesh(
      new THREE.BoxGeometry(0.24, mastH, 0.28),
      darkMetal
    );
    rail.position.set(mastX + rx, mastY, -mastD * 0.5);
    root.add(rail);
  });

  const dockHead = new THREE.Mesh(
    new THREE.BoxGeometry(mastW * 1.25, 0.9, mastD * 1.25),
    darkChassis
  );
  dockHead.position.set(mastX, height + mastH - 0.45, 0);
  root.add(dockHead);

  // 7. Top Illuminated Status Identification Badge (Matching Image 2)
  const badgeW = width * 0.45;
  const badgeH = 1.4;
  const badgeZ = -depth * 0.48 - 0.04;
  const badgeY = height - 0.8;

  const toolBadge = new THREE.Mesh(
    new THREE.BoxGeometry(badgeW, badgeH, 0.08),
    darkChassis
  );
  toolBadge.position.set(chamberX, badgeY, badgeZ);
  root.add(toolBadge);

  const toolBadgeBorder = new THREE.Mesh(
    new THREE.BoxGeometry(badgeW + 0.16, badgeH + 0.16, 0.04),
    ledGreen
  );
  toolBadgeBorder.position.set(chamberX, badgeY, badgeZ - 0.02);
  root.add(toolBadgeBorder);

  const badgeTex = getToolBadgeTexture();
  if (badgeTex) {
    const badgeTexMat = getSharedStandardMaterial({
      color: '#FFFFFF',
      map: badgeTex,
      emissive: '#FFFFFF',
      emissiveIntensity: 0.95,
      toneMapped: false,
    });
    const badgePlane = new THREE.Mesh(
      new THREE.PlaneGeometry(badgeW * 0.96, badgeH * 0.94),
      badgeTexMat
    );
    badgePlane.position.set(chamberX, badgeY, badgeZ - 0.05);
    badgePlane.rotation.y = Math.PI;
    root.add(badgePlane);
  }

  return root;
}

/**
 * Builds the complete, high-fidelity AWP-300 Wafer Processing Tool 3D asset hierarchy.
 */
function buildAwp300Model(): THREE.Group {
  return buildCleanroomWorkcellModel();
}

/**
 * Builds the complete, high-fidelity IndustrialVerticalLifter 3D asset hierarchy.
 */
function buildLifterModel(): THREE.Group {
  const root = new THREE.Group();
  root.name = 'LifterTemplate';

  const { w: width, h: height, d: depth } = LIFTER_TEMPLATE_DIMS;
  const towerW = width * 0.55;
  const towerD = depth * 0.55;
  const baseH = 3.6;
  const mastH = height - baseH;

  const satinGray = getBrushedAluminumMaterial();
  const darkSteel = getIndustrialDarkChassisMaterial();
  const polyGlass = getCleanroomGlassMaterial('#38BDF8', 0.82);
  const beltBlue = getSharedStandardMaterial({
    color: '#0284C7',
    roughness: 0.35,
    metalness: 0.25,
  });

  // 1. Vented Louver Base Skirt
  const baseSkirt = new THREE.Mesh(
    new THREE.BoxGeometry(towerW * 1.1, baseH, towerD * 1.1),
    darkSteel
  );
  baseSkirt.position.set(0, baseH * 0.5, 0);
  root.add(baseSkirt);

  // 2. Tower Corner Mast Columns
  [-1, 1].forEach((sx) => {
    [-1, 1].forEach((sz) => {
      const col = new THREE.Mesh(
        new THREE.BoxGeometry(0.35, mastH, 0.35),
        satinGray
      );
      col.position.set((towerW * 0.5 - 0.2) * sx, baseH + mastH * 0.5, (towerD * 0.5 - 0.2) * sz);
      root.add(col);
    });
  });

  // Intermediate modular tier collars
  const tierCount = 4;
  for (let i = 1; i <= tierCount; i++) {
    const collarY = baseH + (mastH / tierCount) * i;
    const collar = new THREE.Mesh(
      new THREE.BoxGeometry(towerW, 0.25, towerD),
      satinGray
    );
    collar.position.set(0, collarY, 0);
    root.add(collar);
  }

  // Vertical Polycarbonate Safety Panels
  const glassPanel = new THREE.Mesh(
    new THREE.BoxGeometry(towerW * 0.9, mastH * 0.92, 0.06),
    polyGlass
  );
  glassPanel.position.set(0, baseH + mastH * 0.5, towerD * 0.48);
  root.add(glassPanel);

  // Synchronous Drive Timing Belts
  [-1, 1].forEach((side) => {
    const belt = new THREE.Mesh(
      new THREE.BoxGeometry(0.12, mastH * 0.95, 0.2),
      beltBlue
    );
    belt.position.set(side * (towerW * 0.35), baseH + mastH * 0.5, 0);
    root.add(belt);
  });

  // Horizontal Infeed Conveyor Bed
  const convLen = width * 0.85;
  const convBed = new THREE.Mesh(
    new THREE.BoxGeometry(convLen, 0.6, towerD * 0.9),
    satinGray
  );
  convBed.position.set(-convLen * 0.5, baseH + 0.8, 0);
  root.add(convBed);

  return root;
}

// ============================================================================
// SUB-MESH PARTS REGISTRATION (EXTRACT ONCE, SHARE EVERYWHERE)
// ============================================================================

function getOrRegisterParts(typeKey: string, builder: () => THREE.Group): SubMeshPart[] {
  let parts = instancedAssetManager.getAssetParts(typeKey);
  if (!parts) {
    const model = builder();
    parts = instancedAssetManager.registerAsset(typeKey, model);
  }
  return parts;
}

export function getOrRegisterEquipmentParts(
  typeKey: 'CleanroomWorkcell' | 'Awp300' | 'IndustrialVerticalLifter3D'
): SubMeshPart[] {
  const fallback = () => {
    if (typeKey === 'CleanroomWorkcell') return getOrRegisterParts(typeKey, buildCleanroomWorkcellModel);
    if (typeKey === 'Awp300') return getOrRegisterParts(typeKey, buildAwp300Model);
    return getOrRegisterParts(typeKey, buildLifterModel);
  };
  return getAmhsKitTemplateParts(typeKey, fallback);
}

// ============================================================================
// MAIN INSTANCED EQUIPMENT COMPONENT
// ============================================================================

export type InstancedEquipmentProps = {
  descriptors: EquipmentRenderDescriptor[];
  excludeIds?: ReadonlySet<string>;
  layoutMeta?: Record<string, Pick<LayoutElement, 'metadata' | 'customProperties'>>;
  onSelect?: (id: string) => void;
};

export const InstancedEquipment: React.FC<InstancedEquipmentProps> = React.memo(
  ({ descriptors, excludeIds, layoutMeta, onSelect }) => {
    const layoutElements = useAppSelector((s) => s.layout.elements);
    const kitRevision = useAmhsKitTemplateRevision();

    const workcellParts = useMemo(
      () => getOrRegisterEquipmentParts('CleanroomWorkcell'),
      [kitRevision]
    );
    const awp300Parts = useMemo(
      () => getOrRegisterEquipmentParts('Awp300'),
      [kitRevision]
    );
    const lifterParts = useMemo(
      () => getOrRegisterEquipmentParts('IndustrialVerticalLifter3D'),
      [kitRevision]
    );

    // Partition descriptors and calculate instance world transforms
    const { workcellInstances, awp300Instances, lifterInstances } = useMemo(() => {
      const workcells: InstanceTransform[] = [];
      const awp300s: InstanceTransform[] = [];
      const lifters: InstanceTransform[] = [];

      for (const d of descriptors) {
        if (d.kind === 'stocker') continue;
        if (excludeIds?.has(d.id)) continue;

        const meta = layoutMeta?.[d.id] ?? layoutElements[d.id];
        const kind = resolveEquipmentKitKind(d, meta);
        const transform = getEquipmentKitWorldTransform(d);
        const box = resolveKitBox(d);

        if (kind === 'lifter') {
          lifters.push({
            id: d.id,
            position: [transform.x, transform.y, transform.z],
            rotation: [0, transform.rotationY, 0],
            scale: [
              box.w / LIFTER_TEMPLATE_DIMS.w,
              box.h / LIFTER_TEMPLATE_DIMS.h,
              box.d / LIFTER_TEMPLATE_DIMS.d,
            ],
          });
        } else if (kind === 'fabProcess') {
          awp300s.push({
            id: d.id,
            position: [transform.x, transform.y, transform.z],
            rotation: [0, transform.rotationY, 0],
            scale: [
              box.w / AWP300_TEMPLATE_DIMS.w,
              box.h / AWP300_TEMPLATE_DIMS.h,
              box.d / AWP300_TEMPLATE_DIMS.d,
            ],
          });
        } else {
          workcells.push({
            id: d.id,
            position: [transform.x, transform.y, transform.z],
            rotation: [0, transform.rotationY, 0],
            scale: [
              box.w / WORKCELL_TEMPLATE_DIMS.w,
              box.h / WORKCELL_TEMPLATE_DIMS.h,
              box.d / WORKCELL_TEMPLATE_DIMS.d,
            ],
          });
        }
      }

      return {
        workcellInstances: workcells,
        awp300Instances: awp300s,
        lifterInstances: lifters,
      };
    }, [descriptors, excludeIds, layoutMeta, layoutElements]);

    if (
      workcellInstances.length === 0 &&
      awp300Instances.length === 0 &&
      lifterInstances.length === 0
    ) {
      return null;
    }

    return (
      <group name="InstancedEquipment">
        {workcellInstances.length > 0 && (
          <InstancedMultiMeshGroup
            layerId="equipment-workcell"
            parts={workcellParts}
            instances={workcellInstances}
            onSelect={onSelect}
          />
        )}
        {awp300Instances.length > 0 && (
          <InstancedMultiMeshGroup
            layerId="equipment-awp300"
            parts={awp300Parts}
            instances={awp300Instances}
            onSelect={onSelect}
          />
        )}
        {lifterInstances.length > 0 && (
          <InstancedMultiMeshGroup
            layerId="equipment-lifter"
            parts={lifterParts}
            instances={lifterInstances}
            onSelect={onSelect}
          />
        )}
      </group>
    );
  }
);

InstancedEquipment.displayName = 'InstancedEquipment';
export default InstancedEquipment;
